import React, { useState, useCallback, useEffect } from 'react';
import { playbookApi } from '../api/playbookApi';
import type { ValidationResult, PlaybookEntity } from '../types/playbook.types';

interface PublishValidationModalProps {
  isOpen: boolean;
  playbook: PlaybookEntity;
  onClose: () => void;
  onPublished: (pb: PlaybookEntity) => void;
}

type Phase = 'validating' | 'errors' | 'confirm' | 'publishing' | 'done';

const PublishValidationModal: React.FC<PublishValidationModalProps> = ({
  isOpen, playbook, onClose, onPublished,
}) => {
  const [phase, setPhase] = useState<Phase>('validating');
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [runSimulation, setRunSimulation] = useState(false);

  const validate = useCallback(async () => {
    setPhase('validating');
    try {
      const res = await playbookApi.validatePlaybook(playbook.id);
      const data = res as any as ValidationResult;
      setValidation(data);
      if (!data.valid) {
        setPhase('errors');
      } else {
        setPhase('confirm');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Validation failed');
      setPhase('errors');
    }
  }, [playbook.id]);

  useEffect(() => {
    if (isOpen) validate();
  }, [isOpen, validate]);

  const handlePublish = useCallback(async () => {
    setPhase('publishing');
    try {
      const res = await playbookApi.publishPlaybook(playbook.id);
      const data = res as any as PlaybookEntity;
      setPhase('done');
      onPublished(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Publish failed');
      setPhase('errors');
    }
  }, [playbook.id, onPublished]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-lg rounded-lg bg-white shadow-xl dark:bg-gray-800">
        <div className="border-b border-gray-200 dark:border-gray-700 px-6 py-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Publish Playbook</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">{playbook.name} v{playbook.version}</p>
        </div>

        <div className="px-6 py-4 space-y-4">
          {/* Validating */}
          {phase === 'validating' && (
            <div className="flex items-center py-4">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-lex-accent border-t-transparent" />
              <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">Validating playbook structure...</span>
            </div>
          )}

          {/* Validation errors */}
          {phase === 'errors' && (
            <div className="space-y-3">
              {error && (
                <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-200">
                  {error}
                </div>
              )}
              {validation && !validation.valid && (
                <>
                  <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 dark:border-rose-800 dark:bg-rose-900/20">
                    <h4 className="text-xs font-semibold text-rose-700 dark:text-rose-300 mb-1">
                      Validation Failed — {validation.errors.length} error{validation.errors.length !== 1 ? 's' : ''}
                    </h4>
                    {validation.errors.map((e, i) => (
                      <div key={i} className="text-xs text-rose-600 dark:text-rose-400 py-0.5">
                        <span className="font-medium">{e.type}:</span> {e.message}
                      </div>
                    ))}
                  </div>
                  {validation.warnings.length > 0 && (
                    <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 dark:border-amber-800 dark:bg-amber-900/20">
                      <h4 className="text-xs font-semibold text-amber-700 dark:text-amber-300 mb-1">
                        Warnings ({validation.warnings.length})
                      </h4>
                      {validation.warnings.map((w, i) => (
                        <div key={i} className="text-xs text-amber-600 dark:text-amber-400 py-0.5">
                          {w.message}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Confirm */}
          {phase === 'confirm' && (
            <div className="space-y-3">
              <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 dark:border-emerald-800 dark:bg-emerald-900/20">
                <p className="text-sm text-emerald-700 dark:text-emerald-300">
                  Validation passed. Ready to publish.
                </p>
              </div>
              {validation?.warnings && validation.warnings.length > 0 && (
                <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 dark:border-amber-800 dark:bg-amber-900/20">
                  {validation.warnings.map((w, i) => (
                    <div key={i} className="text-xs text-amber-600 dark:text-amber-400 py-0.5">{w.message}</div>
                  ))}
                </div>
              )}
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={runSimulation}
                  onChange={(e) => setRunSimulation(e.target.checked)}
                  className="rounded border-gray-300 text-lex-accent focus:ring-lex-accent"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Run test simulation before publishing?
                </span>
              </label>
            </div>
          )}

          {/* Publishing */}
          {phase === 'publishing' && (
            <div className="flex items-center py-4">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-emerald-600 border-t-transparent" />
              <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">Publishing...</span>
            </div>
          )}

          {/* Done */}
          {phase === 'done' && (
            <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 dark:border-emerald-800 dark:bg-emerald-900/20">
              <p className="text-sm font-medium text-emerald-700 dark:text-emerald-300">
                Published successfully!
              </p>
            </div>
          )}
        </div>

        <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-3 flex justify-end gap-2">
          {phase === 'errors' && (
            <button type="button" onClick={onClose}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700">
              Close
            </button>
          )}
          {phase === 'confirm' && (
            <>
              <button type="button" onClick={onClose}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700">
                Cancel
              </button>
              <button type="button" onClick={handlePublish}
                className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700">
                Publish Now
              </button>
            </>
          )}
          {phase === 'done' && (
            <button type="button" onClick={onClose}
              className="rounded-md bg-lex-accent px-4 py-2 text-sm font-medium text-lex-accent-contrast hover:bg-lex-accent-hover">
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PublishValidationModal;
